import api from 'api/apiClient';
import { ensureFileId } from 'modules/shared/service';
import { formatDate } from 'modules/shared/utils';

const unwrap = async (promise) => {
  const res = await promise;
  return res.data;
};

export const getCandidateInterviewDetails = async (id) => {
  if (!id) throw new Error('Candidate id is required');
  const { data: raw } = await unwrap(await api.get(`/api/hr/onboarding/${id}`));

  return {
    id: raw.id,
    currentInterviewRound: raw.current_interview_round,
    interviewAt: raw.next_interview,
    jobPostingId: raw.job_posting_id,
    round1: {
      integratedReasoning: raw.round_1?.integrated_reasoning,
      quantitativeReasoning: raw.round_1?.quantitative_reasoning,
      verbalReasoning: raw.round_1?.verbal_reasoning,
      remark: raw.round_1?.remark || '',
    },
    round2: {
      technicalKnowledge: raw.round_2?.technical_knowledge,
      teamPlayer: raw.round_2?.team_player,
      problemSolvingSkills: raw.round_2?.problem_solving_skills,
      peopleSkills: raw.round_2?.people_skills,
      remark: raw.round_2?.remark || '',
    },
    round3: (raw.round_3 ?? []).map((r) => ({
      salaryFinalized: r.salary_finalized,
      offerLetter: {
        status: r.offer_letter?.status,
        id: r.offer_letter?.id,
        name: r.offer_letter?.name,
        url: r.offer_letter?.url,
        sendDate: r.offer_letter?.send_date,
        acceptedDate: r.offer_letter?.accepted_date,
        rejectedDate: r.offer_letter?.rejected_date,
      },
    })),
    contract: (raw.contract ?? []).map((c) => ({
      status: c.status,
      id: c.id,
      name: c.name,
      url: c.url,
      sendDate: c.send_date,
      acceptedDate: c.accepted_date,
      rejectedDate: c.rejected_date,
    })),
    induction: {
      backgroundVerification: raw.induction?.background_verification,
      biometrics: raw.induction?.biometrics,
      joiningDate: raw.induction?.joining_date,
      joiningKit: raw.induction?.joining_kit
        ? raw.induction?.joining_kit.map((d) => ({
            name: d.name,
            id: d.id,
            url: d.url,
            status: d.status,
            handoverDate: d.handover_date,
            uploadedBy: d.uploaded_by,
            uploadedDate: d.uploaded_date,
          }))
        : [],
    },
    personalData: {
      formStatus: raw.personal_data?.status,
      name: raw.personal_data?.name ?? '',
      dateOfBirth: raw.personal_data?.date_of_birth
        ? formatDate(raw.personal_data?.date_of_birth, 'YYYY-MM-DD')
        : null,
      age: raw.personal_data?.age ?? '',
      gender: raw.personal_data?.gender ?? '',
      contact: raw.personal_data?.contact ?? '',
      email: raw.personal_data?.email ?? '',
      photo: raw.personal_data?.photo ?? null,
      residentialAddress: raw.personal_data?.residential_address ?? '',
      permanentAddress: raw.personal_data?.permanent_address ?? '',
      nationality: raw.personal_data?.nationality ?? '',
      medicalCondition: raw.personal_data?.medical_condition ?? '',
      aadhaarNumber: raw.personal_data?.aadhaar_number ?? '',
      panNumber: raw.personal_data?.pan_number ?? '',
      documents: raw.personal_data?.documents
        ? raw.personal_data?.documents.map((d) => ({
            name: d.name,
            id: d.id,
            url: d.url,
            uploadedBy: d.uploaded_by,
            uploadedDate: d.uploaded_date,
          }))
        : [],
    },
    salaryCap: raw.max_ctc,
  };
};

function normalizeScore(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function normalizeCriterion(criterion) {
  if (!criterion) return undefined;

  return {
    score: normalizeScore(criterion.score),
    remark: criterion.remark ?? '',
  };
}

const normalizeCandidateInterviewPayload = async (data) => {
  return {
    id: data.id,
    round_1: data.round1 && {
      integrated_reasoning: normalizeCriterion(data.round1.integratedReasoning),
      quantitative_reasoning: normalizeCriterion(data.round1.quantitativeReasoning),
      verbal_reasoning: normalizeCriterion(data.round1.verbalReasoning),
      remark: data.round1.remark ?? '',
    },
    round_2: data.round2 && {
      technical_knowledge: normalizeCriterion(data.round2.technicalKnowledge),
      team_player: normalizeCriterion(data.round2.teamPlayer),
      problem_solving_skills: normalizeCriterion(data.round2.problemSolvingSkills),
      people_skills: normalizeCriterion(data.round2.peopleSkills),
      remark: data.round2.remark ?? '',
    },
    round_3: Array.isArray(data.round3)
      ? data.round3.map((item) => ({
          salary_finalized: Number(item.salaryFinalized) || 0,
          offer_letter: item.offerLetter
            ? {
                status: item.offerLetter.status,
                id: item.offerLetter.id,
                name: item.offerLetter.name,
                url: item.offerLetter.url,
                send_date: item.offerLetter.sendDate,
                accepted_date: item.offerLetter.acceptedDate,
                rejected_date: item.offerLetter.rejectedDate,
              }
            : null,
        }))
      : [],
    contract: (data.contract ?? []).map((item) => ({
      status: item.status,
      id: item.id,
      name: item.name,
      url: item.url,
      send_date: item.sendDate,
      accepted_date: item.acceptedDate,
      rejected_date: item.rejectedDate,
    })),
    induction: {
      background_verification: data.induction.backgroundVerification,
      biometrics: data.induction.biometrics,
      joining_date: data.induction.joiningDate ? formatDate(data.induction.joiningDate, 'YYYY-MM-DD') : null,
      joining_kit: (data.induction.joiningKit ?? []).map((k) => ({
        name: k.name,
        id: k.id,
        status: k.status,
        handover_date: k.handoverDate,
      })),
    },
    personal_data: {
      status: data.personalData.formStatus,
      name: data.personalData.name ?? '',
      date_of_birth: data.personalData.dateOfBirth,
      age: data.personalData.age ?? '',
      gender: data.personalData.gender ?? '',
      contact: data.personalData.contact ?? '',
      email: data.personalData.email ?? '',
      photo: await ensureFileId(data.personalData.photo),
      residential_address: data.personalData.residentialAddress ?? '',
      permanent_address: data.personalData.permanentAddress ?? '',
      nationality: data.personalData.nationality ?? '',
      medical_condition: data.personalData.medicalCondition ?? '',
      aadhaar_number: data.personalData.aadhaarNumber ?? '',
      pan_number: data.personalData.panNumber ?? '',
      documents: data.personalData.documents
        ? data.personalData.documents.map((d) => ({
            name: d.name,
            id: d.id,
            uploaded_by: d.uploadedBy,
            uploaded_date: d.uploadedDate,
          }))
        : [],
    },
    current_interview_round: data.currentInterviewRound || 0,
    next_interview: data.nextInterview,
  };
};

export const updateCandidateInterviewDetails = async (id, data) => {
  if (!id || !data) throw new Error('Candidate id and data is required');
  const payload = await normalizeCandidateInterviewPayload({ ...data, id });
  await unwrap(await api.put(`/api/hr/onboarding/${id}/update`, payload));
};

export async function generateEmployeeCredentials(applicantId) {
  if (!applicantId) throw new Error('Applicant ID is required');

  const res = await unwrap(await api.get(`/api/hr/onboarding/${applicantId}/credentials`));

  return {
    employeeId: res.data?.employee_id,
    password: res.data?.password,
  };
}

export const completeEmployeeOnboarding = async (applicationId, data) => {
  if (!applicationId || !data) throw new Error('Application id and data is required');
  const payload = {
    applicant_id: applicationId,
    employee_id: data.employeeId,
    password: data.password,
  };

  await unwrap(await api.post(`/api/hr/onboarding/${applicationId}/complete`, payload));
};
